
import { supabase } from "@/integrations/supabase/client";
import { TerraceSubmission } from "@/types/terrace";
import { TerraceFormValues } from "./SubmissionFormSchema";
import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

export const useSubmissionApproval = (fetchSubmissions: () => void) => {
  const handleApprove = async (
    selectedSubmission: TerraceSubmission | null, 
    form: UseFormReturn<TerraceFormValues>,
    setIsDialogOpen: (open: boolean) => void
  ) => {
    if (!selectedSubmission) return;

    try {
      // Get current form values to include any updates (like estimated orientation)
      const formValues = form.getValues();
      
      // Step 1: Update the submission with any form changes and set status to 'approved'
      const updateData = {
        status: "approved",
        // Update orientation if it was estimated during review
        orientation: formValues.orientation,
        // Update other fields that might have been modified
        name: formValues.name,
        address: formValues.address,
        openinghours: formValues.openingHours,
        detailed_opening_hours: formValues.detailedOpeningHours,
        terracedescription: formValues.terraceDescription,
        website: formValues.website,
        phone: formValues.phone,
        hasfood: formValues.hasFood,
        petfriendly: formValues.petFriendly,
        servesalcohol: formValues.servesAlcohol,
        sunstatus: formValues.sunStatus,
        photo_url: formValues.photo_url
      };

      const { error: updateError } = await supabase
        .from("terrace_submissions")
        .update(updateData)
        .eq("id", selectedSubmission.id);

      if (updateError) {
        console.error("Error approving submission:", updateError);
        toast.error("Failed to approve submission");
        return;
      }

      // Step 2: Wait a moment for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 3: Verify the terrace was created in the terraces table
      const { data: terraceData, error: terraceError } = await supabase
        .from("terraces")
        .select("id")
        .eq("submission_id", selectedSubmission.id)
        .single();

      if (terraceError) {
        console.error("Error checking terrace creation:", terraceError);
        toast.error("Terrace may not have been properly created. Please check manually.");
      } else if (terraceData) {
        // Step 4: Only delete the submission if terrace was successfully created
        const { error: deleteError } = await supabase
          .from("terrace_submissions")
          .delete()
          .eq("id", selectedSubmission.id);

        if (deleteError) {
          console.error("Error deleting submission:", deleteError);
          // Don't show error to user since the main operation succeeded
        }
      }

      toast.success("Submission approved and terrace published successfully!");
      setIsDialogOpen(false);
      fetchSubmissions();
    } catch (error) {
      console.error("Error approving submission:", error);
      toast.error("Failed to approve submission");
    }
  };

  const handleReject = async (selectedSubmission: TerraceSubmission | null, setIsDialogOpen: (open: boolean) => void) => {
    if (!selectedSubmission) return;

    try {
      const { error } = await supabase
        .from("terrace_submissions")
        .update({ status: "rejected" })
        .eq("id", selectedSubmission.id);

      if (error) {
        console.error("Error rejecting submission:", error);
        toast.error("Failed to reject submission");
        return;
      }

      toast.success("Submission rejected");
      setIsDialogOpen(false);
      fetchSubmissions();
    } catch (error) {
      console.error("Error rejecting submission:", error);
      toast.error("Failed to reject submission");
    }
  };

  return {
    handleApprove,
    handleReject,
  };
};
